// @ts-nocheck
import React, { useCallback, useMemo, useState } from 'react';
import {
  Box, Stack, Typography, useMediaQuery, useTheme,
} from '@mui/material';
import { formatDate } from '../../helpers/dateHelper';
import { getReportFormIdString, REPORT_STATUS_NAMES } from '../../constants/lkCadastr/cadastrStorage';
import useGetMember from '../../hooks/useGetMember';
import CadastrAdminTable from '../../components/CadastrAdmin/CadastrAdminTable';
import CustomCheckbox from '../../components/CustomCheckbox';
import CustomDatePicker from '../../components/CustomDatePicker';
import Selector from '../../components/Selectors/Selector';
import { useApi } from '../../hooks';
import {
  cadastreReportHistoryRequest,
  downloadProcuration,
  downloadReportPDF,
  getCadastreReportStatusesRequest,
} from '../../api/cadastreApi';
import { METHODS_API } from '../../api/api';
import { CellStatus } from '../../components/Cadastr/CellStatus';
import { valueToString } from '../../helpers/stringHelper';
import { useTableStore, useUserStore } from '../../store';
import { USER_TEST_ADMIN } from '../../constants/testAdminContants';
import { LightTooltip } from '../../components/LightTooltip';
import CustomButton from '../../components/Buttons/CustomButton';
import { downloadBlobFileOnClick } from '../../helpers';
import { ReactComponent as InfoIcon } from '../../assets/info.svg';
import { ReactComponent as PdfFileIcon } from '../../assets/pdf_file.svg';

const initialReportFilter = {
  search: '',
  report_status_id: '',
  send_date_from: null,
  send_date_to: null,
  confirmed_date_from: null,
  confirmed_date_to: null,
  date_from: null,
  date_to: null,
  delay_date_from: null,
  delay_date_to: null,
  is_delayed: '',
};

export default function CadastrAdminReportsPage() {
  const theme = useTheme();
  const [reports, setReports] = useState([]);
  const { userStore } = useUserStore();
  const [reportFilter, setReportFilter] = useState(initialReportFilter);
  const [reportStatuses, setReportStatuses] = useState('');
  const [loadingReports, setLoadingReports] = useState({});
  const tabletSizeAndLower = useMediaQuery(theme.breakpoints.down('md'));
  const { tableStore, setTableStore } = useTableStore();

  useGetMember();

  const isAdminEditor = userStore?.email === USER_TEST_ADMIN;

  const { makeRequest: getReportHistories, isLoading } = useApi({
    setter: setReports,
    request: (outerParams) => {
      const params = {
        ...reportFilter,
        report_status_id: reportFilter?.report_status_id?.id,
        ...outerParams?.params,
        page: tableStore.page,
        total: tableStore.perPage,
      };

      if (typeof params.is_delayed === 'object' && params.is_delayed?.value) {
        params.is_delayed = params.is_delayed.value;
      }

      if (!isAdminEditor) {
        params.filling_percentage = 100;
        params.exclude_status_codes = REPORT_STATUS_NAMES.formation;
      }

      return cadastreReportHistoryRequest({
        setTableStore,
        params,
      });
    },
    cancelPrevious: true,
    deps: [tableStore.page, tableStore.perPage],
    shouldRequest: userStore?.email,
    debounceTime: 1000,
  });

  const { defaultRequest: changeReportHistory } = useApi({
    request: cadastreReportHistoryRequest,
  });

  const changeStatus = (id) => async (e, report_status_id) => {
    e.stopPropagation();

    const newReport = await changeReportHistory({
      method: METHODS_API.PUT,
      id,
      data: { report_status_id: report_status_id.id },
    });

    const newReports = reports.slice();
    const reportIdx = reports.findIndex((it) => it.id === id);
    newReports.splice(reportIdx, 1, newReport);
    setReports(newReports);
  };

  const changeDelayStatus = async function (id, event, value) {
    event.stopPropagation();

    const newReport = await changeReportHistory({
      method: METHODS_API.PUT,
      id,
      data: { is_delayed: value },
    });

    const newReports = reports.slice();
    const reportIdx = reports.findIndex((it) => it.id === id);
    newReports.splice(reportIdx, 1, newReport);
    setReports(newReports);
  };

  const changeDateOfDelay = async function (id, event, value) {
    if (Number.isNaN(Date.parse(value))) {
      return;
    }

    const newReport = await changeReportHistory({
      method: METHODS_API.PUT,
      id,
      data: { date_of_delay: value },
    });

    const newReports = reports.slice();
    const reportIdx = reports.findIndex((it) => it.id === id);
    newReports.splice(reportIdx, 1, newReport);
    setReports(newReports);
  };

  const unusedStatusesNames = ['проверен, ожидается бумажный документ', 'бумажный документ распечатан', 'бумажный документ принят', !isAdminEditor ? 'формируется' : ''];
  const statusOptions = useMemo(() => (typeof reportStatuses === 'string' ? [] : reportStatuses?.reduce((acc, status) => {
    if (acc.some((item) => item.name === status.name)
            || unusedStatusesNames.includes(status.name.toLowerCase())) return [...acc, { ...status, isFilter: true }];
    return [...acc, status];
  }, [])), [reportStatuses]);

  const setLoadingReport = (reportId, isLoading) => {
    setLoadingReports((prev) => ({ ...prev, [reportId]: isLoading }));
  };

  const reportsTableColumns = useCallback((downloadFile, downloadReportRequest, loadingReports, setLoadingReport) => [
    {
      headerTitle: 'Субъект',
      field: 'subject',
      getField: ({ subject_attributes }) => subject_attributes?.attributes?.name,
    },
    {
      headerTitle: 'Номер отчёта',
      field: 'id',
      getField: ({ id, created_at }) => {
        const year = new Date(created_at)?.getFullYear();
        return `${id}/${year ? year - 1 : 0}`;
      },
    },
    {
      headerTitle: 'Дата подачи',
      field: 'application_date',
      getField: ({ application_date }) => formatDate({ date: application_date }),
    },
    {
      headerTitle: 'Дата принятия',
      field: 'approved_date',
      getField: ({ approved_date }) => formatDate({ date: approved_date }),
    },
    {
      headerTitle: 'Статус',
      field: 'status',
      // eslint-disable-next-line react/no-unstable-nested-components
      getComponent: ({ id, report_status_attributes }) => (
        <Selector
          value={report_status_attributes?.attributes}
          optionField={{ value: 'code', id: 'code', label: 'name' }}
          onChange={changeStatus(id)}
          options={statusOptions}
        />
      ),
    },
    {
      headerTitle: 'Заполнено',
      field: 'complete',
      // eslint-disable-next-line react/no-unstable-nested-components
      getField: ({ filling_percentage }) => <CellStatus complete={filling_percentage} />,
    },
    {
      headerTitle: 'Управление',
      getHeaderTitle: () => (
        <Stack direction="row" justifyContent="center" alignItems="center" gap="6px">
          <Typography sx={{
            color: '#ED6C02',
            fontWeight: 600,
          }}
          >
            Управление
          </Typography>
          <LightTooltip
            title="При принятии отчёта будет автоматически создан подписанный PDF-документ, доступный для скачивания"
            placement="right"
          >
            <InfoIcon />
          </LightTooltip>
        </Stack>
      ),
      getField: ({
        report_status_attributes, id, created_at, ...props
      }) => {
        const reportNumber = getReportFormIdString({ id, created_at, code: report_status_attributes?.attributes?.code });

        return (
          <>
            {[REPORT_STATUS_NAMES.accepted, REPORT_STATUS_NAMES.accepted_rework].includes(report_status_attributes?.attributes?.code) && (
            <CustomButton
              sx={{ height: '30px', minWidth: '220px' }}
              endIcon={<PdfFileIcon />}
              isLoading={loadingReports[id]}
              loadingText="Формируется отчет..."
              onClick={(e) => {
                e.stopPropagation();
                setLoadingReport(id, true);
                downloadReportRequest(id)
                  .then((response) => response.data)
                  .then((data) => downloadBlobFileOnClick(data, `Отчет ${reportNumber}.pdf`))
                  .finally(() => setLoadingReport(id, false));
              }}
            >
              <span style={{ textWrap: 'nowrap' }}>Скачать отчёт</span>
            </CustomButton>
            )}
            <Stack>
              {props.procuration_files_attributes.map((file) => (
                <a
                  key={file.id}
                  style={{ color: '#1976D2' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    downloadFile(file.id, id)
                      .then((response) => response.data)
                      .then((data) => downloadBlobFileOnClick(data, file.filename));
                  }}
                  download
                >
                  {file.filename}
                </a>
              ))}
            </Stack>
          </>
        );
      },
    },
  ], [changeDateOfDelay, changeDelayStatus, changeStatus, statusOptions]);

  useApi({
    request: getCadastreReportStatusesRequest,
    setter: (data) => {
      setReportStatuses(isAdminEditor ? data
        : data.filter((status) => status.code !== REPORT_STATUS_NAMES.formation));
    },
    shouldRequest: userStore?.email,
  });

  const changeFilter = (filterName) => (value, noSearch) => {
    const newReportFilter = { ...reportFilter, [filterName]: value };
    setReportFilter(newReportFilter);

    const params = {};

    if (newReportFilter.search) params.search = newReportFilter.search;
    if (newReportFilter.date_from) params.date_from = valueToString(newReportFilter.date_from);
    if (newReportFilter.date_to) params.date_to = valueToString(newReportFilter.date_to);
    if (newReportFilter.report_status_id?.id) params.report_status_id = newReportFilter.report_status_id?.id;
    if (newReportFilter.is_delayed) params.is_delayed = newReportFilter.is_delayed?.value;

    if (!noSearch) getReportHistories({ params });
  };

  const clearReportFilter = () => {
    setReportFilter(initialReportFilter);
    getReportHistories();
  };

  const downloadProcurationFile = (fileId, reportId) => downloadProcuration(reportId, { file_id: fileId });
  const downloadReportRequest = (reportId) => downloadReportPDF(reportId);

  return (
    <Box pt={1} display="flex" justifyContent="space-between" flexWrap={tabletSizeAndLower && 'wrap'}>
      <CadastrAdminTable
        title="Отчёты"
        unusedStatuses={unusedStatusesNames}
        isLoading={isLoading}
        setReports={setReports}
        getReports={getReportHistories}
        clearFilter={clearReportFilter}
        reportStatuses={reportStatuses}
        filter={reportFilter}
        changeFilter={changeFilter}
        tableData={reports}
        tableColumns={reportsTableColumns(downloadProcurationFile, downloadReportRequest, loadingReports, setLoadingReport)}
      />
    </Box>
  );
}
